import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

type AgentPreset = {
	key: string;
	label: string;
	defaultModel: string;
	defaultProvider?: string;
	defaultPrompt: string;
};

type AgentOverrideEdit = {
	model?: string;
	provider?: string;
	promptOverride?: string;
};

type OverrideEditState = Record<string, AgentOverrideEdit>;

type OverrideArgValue = {
	model?: string;
	provider?: string;
	promptOverride?: string;
};

function buildOverrideArg(
	presets: AgentPreset[],
	edits: OverrideEditState,
): Record<string, OverrideArgValue> | undefined {
	const out: Record<string, OverrideArgValue> = {};
	for (const preset of presets) {
		const edit = edits[preset.key] ?? {};
		const defaultProvider = preset.defaultProvider ?? "";
		const model = (edit.model ?? preset.defaultModel).trim();
		const provider = (edit.provider ?? defaultProvider).trim();
		const promptOverride = (edit.promptOverride ?? preset.defaultPrompt).trim();
		const value: OverrideArgValue = {};
		if (model !== preset.defaultModel.trim()) value.model = model;
		if (provider !== defaultProvider.trim()) value.provider = provider;
		if (promptOverride !== preset.defaultPrompt.trim()) {
			value.promptOverride = promptOverride;
		}
		if (Object.keys(value).length > 0) out[preset.key] = value;
	}
	return Object.keys(out).length > 0 ? out : undefined;
}

function AgentOverrideSection({
	preset,
	edit,
	onChange,
	onReset,
}: {
	preset: AgentPreset;
	edit: AgentOverrideEdit;
	onChange: (next: AgentOverrideEdit) => void;
	onReset: () => void;
}) {
	const [open, setOpen] = useState(false);
	const defaultProvider = preset.defaultProvider ?? "";
	const modelValue = edit.model ?? preset.defaultModel;
	const providerValue = edit.provider ?? defaultProvider;
	const promptValue = edit.promptOverride ?? preset.defaultPrompt;
	const isModelModified = modelValue !== preset.defaultModel;
	const isProviderModified = providerValue !== defaultProvider;
	const isPromptModified = promptValue !== preset.defaultPrompt;
	const isModified = isModelModified || isProviderModified || isPromptModified;

	return (
		<Collapsible
			open={open}
			onOpenChange={setOpen}
			className="rounded-lg border border-border"
		>
			<CollapsibleTrigger
				render={
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-auto w-full justify-between rounded-lg rounded-b-none px-3 py-2 hover:bg-muted/50"
					/>
				}
			>
				<div className="flex min-w-0 flex-col items-start gap-0.5">
					<span className="flex items-center gap-2 text-sm font-medium">
						{preset.label}
						{isModified ? (
							<span className="rounded-full bg-warning/20 px-1.5 py-0.5 text-[10px] font-medium text-warning">
								modified
							</span>
						) : null}
					</span>
					<span className="truncate text-xs font-normal text-muted-foreground">
						{modelValue}
					</span>
				</div>
				<ChevronDownIcon
					className={`size-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className="flex flex-col gap-3 border-t border-border p-3">
					<Field>
						<FieldLabel
							htmlFor={`override-${preset.key}-model`}
							className="text-xs"
						>
							Model
						</FieldLabel>
						<Input
							id={`override-${preset.key}-model`}
							name={`override-${preset.key}-model`}
							placeholder={preset.defaultModel}
							value={modelValue}
							onChange={(e) => onChange({ ...edit, model: e.target.value })}
						/>
					</Field>
					<Field>
						<FieldLabel
							htmlFor={`override-${preset.key}-provider`}
							className="text-xs"
						>
							Provider (OpenRouter routing)
						</FieldLabel>
						<Input
							id={`override-${preset.key}-provider`}
							name={`override-${preset.key}-provider`}
							placeholder={defaultProvider || "auto"}
							value={providerValue}
							onChange={(e) => onChange({ ...edit, provider: e.target.value })}
						/>
					</Field>
					<Field>
						<FieldLabel
							htmlFor={`override-${preset.key}-prompt`}
							className="text-xs"
						>
							Prompt
						</FieldLabel>
						<MarkdownEditor
							value={promptValue}
							onChange={(md) => onChange({ ...edit, promptOverride: md })}
							placeholder="Prompt body"
							className="[&_.tiptap]:!min-h-[200px] [&_textarea]:!min-h-[200px]"
						/>
					</Field>
					<div className="flex justify-end">
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={!isModified}
							onClick={onReset}
						>
							Reset to defaults
						</Button>
					</div>
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}

export default function RunScenarioDialog({
	scenarioId,
	open,
	onOpenChange,
	onRunStarted,
}: {
	scenarioId: Id<"scenarios">;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onRunStarted?: (clericalThreadId: string, runId: Id<"scenarioRuns">) => void;
}) {
	const targets = useQuery(api.scenarios.listConnectableTargets);
	const presets = useQuery(api.agent.registry.listOverridablePresets);
	const startRun = useMutation(api.scenarios.startRun);
	const [selected, setSelected] = useState<string>("");
	const [edits, setEdits] = useState<OverrideEditState>({});

	const { mutate, isPending, error } = useReactQueryMutation({
		mutationFn: async (target: {
			tenantId: Id<"tenants">;
			userId: Id<"users">;
		}) => {
			const agentOverrides = presets
				? buildOverrideArg(presets, edits)
				: undefined;
			return await startRun({
				scenarioId,
				targetTenantId: target.tenantId,
				targetUserId: target.userId,
				...(agentOverrides ? { agentOverrides } : {}),
			});
		},
		onSuccess: ({ clericalThreadId, runId }) => {
			onOpenChange(false);
			onRunStarted?.(clericalThreadId, runId);
		},
	});

	const list = targets ?? [];
	const selectedTarget = list.find(
		(t) => `${t.tenantId}:${t.userId}` === selected,
	);

	const setEditFor = (key: string, next: AgentOverrideEdit) => {
		setEdits((prev) => ({ ...prev, [key]: next }));
	};

	const resetEditFor = (key: string) => {
		setEdits((prev) => {
			const { [key]: _removed, ...rest } = prev;
			return rest;
		});
	};

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				className="flex w-full flex-col gap-0 p-0 data-[side=right]:sm:max-w-lg"
			>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						if (!selectedTarget || isPending) return;
						mutate({
							tenantId: selectedTarget.tenantId,
							userId: selectedTarget.userId,
						});
					}}
					className="flex h-full min-h-0 flex-col"
				>
					<SheetHeader className="border-b p-4">
						<SheetTitle>Run scenario</SheetTitle>
						<SheetDescription>
							Pick the connected Outlook mailbox the agent should drive. The
							thread runs in eval mode — no real Outlook drafts or calendar
							events are written.
						</SheetDescription>
					</SheetHeader>
					<ScrollArea className="min-h-0 flex-1">
						<div className="p-4">
							<FieldGroup>
								<Field>
									<FieldLabel htmlFor="run-target">
										Target connection
									</FieldLabel>
									{targets === undefined ? (
										<p className="text-sm text-muted-foreground">
											Loading connections…
										</p>
									) : list.length === 0 ? (
										<p className="text-sm text-muted-foreground">
											No connected Outlook mailboxes found. Connect Outlook from
											a firm first.
										</p>
									) : (
										<Select
											value={selected}
											onValueChange={(v) => setSelected(v ?? "")}
										>
											<SelectTrigger id="run-target" className="w-full">
												<SelectValue placeholder="Select a target…" />
											</SelectTrigger>
											<SelectContent
												align="start"
												alignItemWithTrigger={false}
												className="w-(--anchor-width) min-w-(--anchor-width)"
											>
												{list.map((t) => (
													<SelectItem
														key={`${t.tenantId}:${t.userId}`}
														value={`${t.tenantId}:${t.userId}`}
														className="py-2"
													>
														<div className="flex min-w-0 flex-col gap-0.5">
															<span className="truncate text-sm font-medium">
																{t.tenantName} — {t.userName}
															</span>
															{t.accountEmail ? (
																<span className="truncate text-xs text-muted-foreground">
																	{t.accountEmail}
																</span>
															) : null}
														</div>
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								</Field>
								<div className="flex flex-col gap-2">
									<div className="flex flex-col gap-0.5">
										<span className="text-sm font-medium">Agent overrides</span>
										<span className="text-xs text-muted-foreground">
											Expand an agent to edit its model or prompt. Unchanged
											agents use the bundled defaults.
										</span>
									</div>
									{presets === undefined ? (
										<p className="text-sm text-muted-foreground">
											Loading agent presets…
										</p>
									) : (
										<div className="flex flex-col gap-2">
											{presets.map((preset) => (
												<AgentOverrideSection
													key={preset.key}
													preset={preset}
													edit={edits[preset.key] ?? {}}
													onChange={(next) => setEditFor(preset.key, next)}
													onReset={() => resetEditFor(preset.key)}
												/>
											))}
										</div>
									)}
								</div>
								{error ? (
									<p className="text-sm text-destructive">
										{error instanceof Error ? error.message : String(error)}
									</p>
								) : null}
							</FieldGroup>
						</div>
					</ScrollArea>
					<SheetFooter className="flex-row justify-end gap-2 border-t bg-muted/30 p-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!selectedTarget || isPending}>
							{isPending ? "Starting..." : "Start run"}
						</Button>
					</SheetFooter>
				</form>
			</SheetContent>
		</Sheet>
	);
}
